
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Award, ThumbsUp, HandHeart, GraduationCap, Star } from 'lucide-react';

export interface SoftSkill {
  name: string;
  level: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  icon: 'award' | 'thumbs-up' | 'hand-heart' | 'graduation-cap' | 'star';
  description?: string;
}

interface SoftSkillsBadgesProps {
  skills: SoftSkill[];
  showDetails?: boolean;
}

export const SoftSkillsBadges: React.FC<SoftSkillsBadgesProps> = ({ 
  skills, 
  showDetails = false 
}) => {
  const getIcon = (iconName: SoftSkill['icon']) => {
    switch (iconName) {
      case 'award':
        return <Award className="h-3.5 w-3.5 mr-1" />;
      case 'thumbs-up':
        return <ThumbsUp className="h-3.5 w-3.5 mr-1" />;
      case 'hand-heart':
        return <HandHeart className="h-3.5 w-3.5 mr-1" />;
      case 'graduation-cap':
        return <GraduationCap className="h-3.5 w-3.5 mr-1" />;
      case 'star':
      default:
        return <Star className="h-3.5 w-3.5 mr-1" />;
    }
  };

  const getBadgeColor = (level: SoftSkill['level']) => {
    switch (level) {
      case 'beginner':
        return 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200';
      case 'intermediate':
        return 'bg-green-100 text-green-700 hover:bg-green-200 border-green-200';
      case 'advanced':
        return 'bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-200';
      case 'expert':
        return 'bg-amber-100 text-amber-700 hover:bg-amber-200 border-amber-200';
      default:
        return 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-200';
    }
  };

  if (!skills || skills.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {skills.map((skill, index) => (
          <Badge 
            key={`${skill.name}-${index}`}
            variant="outline"
            className={`${getBadgeColor(skill.level)} flex items-center`}
          >
            {getIcon(skill.icon)}
            {skill.name}
            {showDetails && (
              <span className="ml-1 text-xs opacity-75">({skill.level})</span>
            )}
          </Badge>
        ))}
      </div>
      
      {showDetails && (
        <div className="mt-3 grid gap-2">
          {skills.map((skill, index) => (
            skill.description && (
              <div key={`desc-${skill.name}-${index}`} className="text-sm">
                <span className="font-medium">{skill.name}:</span> {skill.description}
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
};

export default SoftSkillsBadges;
